import { useCallback } from 'react';
import { RoiId } from '~/domain/changes/ids';
import { DicomColor } from '~/domain/dicom/dicomColor';
import { DomainError } from '~/domain/errors/DomainError';
import { enqueueSnackbar } from '~/utils/notifications';
import { useRoiService } from './useRoiService';

/**
 * Runs an ROI mutation. The unsaved-changes guard arms itself: the
 * service emits a changeset, PendingChangesStore accumulates it, and
 * PendingChangesRecoilBridge derives hasUnsavedChangesState from it.
 *
 * A DomainError — an approved structure, a review lock on a signed-off
 * set — is shown to the user as a warning and the call returns false;
 * anything else is rethrown. Returns true when the mutation applied.
 *
 * The warning's text may name the structure, and warnings are mirrored to
 * the browser console, so only the error's code is logged.
 */
export function runRoiCommand(op: () => void): boolean {
    try {
        op();
        return true;
    } catch (e) {
        if (e instanceof DomainError) {
            enqueueSnackbar(e.message, {
                variant: 'warning',
                logMessage: `ROI command refused: ${e.code}`,
            });
            return false;
        }
        throw e;
    }
}

export function useRoiCommands() {
    const service = useRoiService();

    const rename = useCallback(
        (id: RoiId, newName: string) => {
            runRoiCommand(() => service.rename({ id }, newName));
        },
        [service]
    );

    const setColor = useCallback(
        (id: RoiId, color: DicomColor) => {
            runRoiCommand(() => service.setColor({ id }, color));
        },
        [service]
    );

    const approve = useCallback(
        (id: RoiId, reviewer: string) => {
            runRoiCommand(() => service.approve({ id }, reviewer));
        },
        [service]
    );

    const reject = useCallback(
        (id: RoiId, reviewer: string) => {
            runRoiCommand(() => service.reject({ id }, reviewer));
        },
        [service]
    );

    const unapprove = useCallback(
        (id: RoiId) => {
            runRoiCommand(() => service.unapprove({ id }));
        },
        [service]
    );

    return { rename, setColor, approve, reject, unapprove };
}
